/**
 * Image Picker Component for Puck Editor
 *
 * Allows users to select images from their site assets, stock library, or upload new ones
 */

import { Check, ImageIcon, Library, Loader2, Upload, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { runUploads, screenImageFiles } from "../../lib/upload-queue";
import { FieldLabel } from "@puckeditor/core";

interface ImagePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

/** As the photo list returns it: `url` is resolved server-side, so there is
 *  nothing left here to reconstruct from a key. */
interface Asset {
  id: string;
  url: string;
  filename: string | null;
  altText: string | null;
}

interface StockImage {
  id: string;
  key: string;
  filename: string | null;
  metadata: {
    url?: string;
    deliveryUrl?: string;
    category?: string;
    tags?: string[];
    altText?: string;
  } | null;
  // The list endpoint flattens the join into an array. The old singular
  // `businessType` field was never on the response, which is why the picker
  // Could not group by trade.
  businessTypes?: { id: string; value: string; label: string }[];
}

type TabType = "your-images" | "stock-images";

/** Sentinel for the trade filter's "everything" option. */
const ALL_TRADES = "all";

const ImagePicker: React.FC<ImagePickerProps> = ({ value = "", onChange, label }) => {
  // Defensive: legacy Puck data may store image fields as objects instead of URL strings.
  // Coerce non-strings to "" so .split / <img src> don't crash the editor.
  const safeValue = typeof value === "string" ? value : "",
    [isOpen, setIsOpen] = useState(false),
    [activeTab, setActiveTab] = useState<TabType>("your-images"),
    [assets, setAssets] = useState<Asset[]>([]),
    [stockImages, setStockImages] = useState<StockImage[]>([]),
    [loading, setLoading] = useState(false),
    [stockLoading, setStockLoading] = useState(false),
    [tradeFilter, setTradeFilter] = useState<string>(ALL_TRADES),
    // Built from the unfiltered load so the options don't shrink as you filter.
    [tradeOptions, setTradeOptions] = useState<{ id: string; label: string }[]>([]),
    [uploading, setUploading] = useState(false),
    [uploadStatus, setUploadStatus] = useState<string | null>(null),
    [error, setError] = useState<string | null>(null),
    [dropdownPos, setDropdownPos] = useState({ left: 0, top: 0, width: 0 }),
    buttonRef = useRef<HTMLButtonElement>(null),
    fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        left: rect.left,
        top: rect.bottom + 4,
        width: Math.max(rect.width, 400),
      });
    }
  }, [isOpen]);

  const fetchAssets = async () => {
      setLoading(true);
      setError(null);
      try {
        const config = (window as any).__SERVICEPRO_CONFIG__;
        if (!config?.businessId || !config?.apiUrl) {
          throw new Error("Site configuration not found");
        }

        const response = await fetch(
          `${config.apiUrl}/api/sites/businesses/${config.businessId}/photos`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch assets");
        }

        const data = await response.json();
        setAssets(data.photos || []);
      } catch (error: any) {
        console.error("Failed to fetch assets:", error);
        setError(error.message || "Failed to load images");
      } finally {
        setLoading(false);
      }
    },
    fetchStockImages = async (businessTypeId?: string) => {
      setStockLoading(true);
      try {
        const config = (window as any).__SERVICEPRO_CONFIG__;
        if (!config?.apiUrl) {
          throw new Error("API configuration not found");
        }

        const params = new URLSearchParams({ limit: "500" });
        if (businessTypeId && businessTypeId !== ALL_TRADES) {
          params.set("businessTypeId", businessTypeId);
        }

        const response = await fetch(`${config.apiUrl}/api/sites/stock-images?${params}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch stock images");
        }

        const data = await response.json(),
          assets: StockImage[] = data.assets || [];
        setStockImages(assets);

        // Only the unfiltered load can see every trade, so that is the one that
        // Defines the options. Filtering must not narrow the menu that produced it.
        if (!businessTypeId || businessTypeId === ALL_TRADES) {
          const byId = new Map<string, string>();
          for (const asset of assets) {
            for (const trade of asset.businessTypes ?? []) {
              byId.set(trade.id, trade.label);
            }
          }
          setTradeOptions(
            Array.from(byId, ([id, label]) => ({ id, label })).sort((a, b) =>
              a.label.localeCompare(b.label),
            ),
          );
        }
      } catch (error: any) {
        console.error("Failed to fetch stock images:", error);
        // Don't show error for stock images - just show empty state
      } finally {
        setStockLoading(false);
      }
    },
    handleOpen = () => {
      setIsOpen(true);
      fetchAssets();
      fetchStockImages();
    },

    /**
     * Uploads every file the picker was handed, a few at a time.
     *
     * Selecting one photo at a time was the only option here, which meant a pro
     * building a gallery reopened this dropdown once per image. The queue reports
     * each file separately, so one bad file no longer sinks the batch.
     */
    uploadOne = async (file: File): Promise<string> => {
      const config = (window as any).__SERVICEPRO_CONFIG__;
      if (!config?.businessId || !config?.apiUrl) {
        throw new Error("Site configuration not found");
      }
      const auth = { Authorization: `Bearer ${localStorage.getItem("token")}` },
        photosUrl = `${config.apiUrl}/api/sites/businesses/${config.businessId}/photos`,

        // The business photo endpoints, not a generic asset write: they meter the
        // Photo limit, verify the bytes reached Cloudflare, dedupe, and queue vision
        // Tagging. A photo registered any other way silently skips all four.
        urlResponse = await fetch(`${photosUrl}/upload-url`, {
          headers: auth,
          method: "POST",
        });
      if (!urlResponse.ok) {
        throw new Error("Failed to get upload URL");
      }

      const { uploadUrl, id: imageId } = await urlResponse.json(),

        formData = new FormData();
      formData.append("file", file);
      const uploadResponse = await fetch(uploadUrl, { body: formData, method: "POST" });
      if (!uploadResponse.ok) {
        throw new Error("Failed to upload image");
      }

      const uploadResult = await uploadResponse.json(),
        variants: string[] = uploadResult.result?.variants ?? [],
        // "public" is the variant safe to render without a signed token. No guessed
        // Fallback URL: one built from a missing account hash renders a broken image
        // And is stored as though it worked.
        deliveryUrl = variants.find((v) => v.endsWith("/public")) ?? variants[0];
      if (!deliveryUrl) {
        throw new Error("Upload returned no URL");
      }

      const registerResponse = await fetch(photosUrl, {
        body: JSON.stringify({
          key: imageId,
          url: deliveryUrl,
          filename: file.name,
          contentType: file.type,
          size: file.size,
        }),
        headers: { "Content-Type": "application/json", ...auth },
        method: "POST",
      });
      if (!registerResponse.ok) {
        throw new Error("Uploaded but could not be saved");
      }

      const photo = await registerResponse.json();
      return photo.url as string;
    },

    getImageUrl = (asset: Asset): string => asset.url,
    getStockImageUrl = (image: StockImage): string => image.metadata?.deliveryUrl || image.metadata?.url || image.key,
    handleSelect = (asset: Asset) => {
      const url = getImageUrl(asset);
      // Close first, then update value to ensure proper state sequencing
      setIsOpen(false);
      // Use setTimeout to ensure the onChange happens after the dropdown close
      // This prevents potential race conditions with React's state batching
      setTimeout(() => {
        onChange(url);
      }, 0);
    },
    handleStockSelect = (image: StockImage) => {
      const url = getStockImageUrl(image);
      // Close first, then update value to ensure proper state sequencing
      setIsOpen(false);
      // Use setTimeout to ensure the onChange happens after the dropdown close
      setTimeout(() => {
        onChange(url);
      }, 0);
    },
    handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(e.target.files ?? []);
      if (selected.length === 0) {
        return;
      }

      setUploading(true);
      setError(null);
      setUploadStatus(null);

      const { accepted, rejected } = screenImageFiles(selected);
      let finished = 0;

      try {
        const outcomes = await runUploads(accepted, async (file) => {
            try {
              return await uploadOne(file);
            } finally {
              finished += 1;
              setUploadStatus(`Uploading ${finished} of ${accepted.length}`);
            }
          }),

          uploaded = outcomes.filter((outcome) => outcome.value),
          failures = outcomes.length - uploaded.length + rejected.length;
        if (failures > 0) {
          setError(
            uploaded.length > 0
              ? `${failures} of ${selected.length} could not be uploaded`
              : "Upload failed",
          );
        }

        if (uploaded.length === 0) {
          return;
        }

        // Refresh so the whole batch is visible, then select the first of them —
        // The picker fills exactly one slot, and the rest are now in the library.
        await fetchAssets();
        const firstUrl = uploaded[0].value!;
        setIsOpen(false);
        setTimeout(() => {
          onChange(firstUrl);
        }, 0);
      } catch (error: any) {
        console.error("Upload failed:", error);
        setError(error.message || "Failed to upload image");
      } finally {
        setUploading(false);
        setUploadStatus(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    };

  return (
    <FieldLabel label={label ?? "Image"} el="div">
      <div style={{ position: "relative" }}>
        {/* Current Image Preview / Trigger */}
        <button
          ref={buttonRef}
          type="button"
          onClick={handleOpen}
          style={{
            alignItems: "center",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "6px",
            color: "#6B7280",
            cursor: "pointer",
            display: "flex",
            fontSize: "14px",
            gap: "8px",
            justifyContent: safeValue ? "flex-start" : "center",
            minHeight: "44px",
            padding: safeValue ? "4px" : "12px",
            width: "100%",
          }}
        >
          {safeValue ? (
            <div style={{ alignItems: "center", display: "flex", gap: "8px", width: "100%" }}>
              <img
                crossOrigin="anonymous"
                src={safeValue}
                alt="Selected"
                style={{
                  borderRadius: "4px",
                  height: "36px",
                  objectFit: "cover",
                  width: "36px",
                }}
              />
              <span
                style={{
                  color: "#374151",
                  flex: 1,
                  fontSize: "13px",
                  overflow: "hidden",
                  textAlign: "left",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {safeValue.split("/")?.[4] || "Selected image"}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#9CA3AF",
                  cursor: "pointer",
                  padding: "4px",
                }}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <>
              <ImageIcon size={18} />
              <span>Select image</span>
            </>
          )}
        </button>

        {/* Dropdown Panel */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              onClick={() => {
                setIsOpen(false);
              }}
              style={{
                bottom: 0,
                left: 0,
                position: "fixed",
                right: 0,
                top: 0,
                zIndex: 999,
              }}
            />

            {/* Panel */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15)",
                display: "flex",
                flexDirection: "column",
                left: `${dropdownPos.left}px`,
                maxHeight: "400px",
                maxWidth: "90vw",
                position: "fixed",
                top: `${dropdownPos.top}px`,
                width: `${dropdownPos.width}px`,
                zIndex: 1000,
              }}
            >
              {/* Header with Tabs and Upload */}
              <div
                style={{
                  borderBottom: "1px solid #E5E7EB",
                }}
              >
                {/* Tab Row */}
                <div
                  style={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 12px 0 12px",
                  }}
                >
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab("your-images");
                      }}
                      style={{
                        alignItems: "center",
                        backgroundColor: activeTab === "your-images" ? "#F3F4F6" : "transparent",
                        border: "none",
                        borderRadius: "4px 4px 0 0",
                        color: activeTab === "your-images" ? "#374151" : "#9CA3AF",
                        cursor: "pointer",
                        display: "flex",
                        fontSize: "12px",
                        fontWeight: 500,
                        gap: "6px",
                        padding: "8px 12px",
                      }}
                    >
                      <ImageIcon size={14} />
                      Your Images
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab("stock-images");
                      }}
                      style={{
                        alignItems: "center",
                        backgroundColor: activeTab === "stock-images" ? "#F3F4F6" : "transparent",
                        border: "none",
                        borderRadius: "4px 4px 0 0",
                        color: activeTab === "stock-images" ? "#374151" : "#9CA3AF",
                        cursor: "pointer",
                        display: "flex",
                        fontSize: "12px",
                        fontWeight: 500,
                        gap: "6px",
                        padding: "8px 12px",
                      }}
                    >
                      <Library size={14} />
                      Stock Library
                    </button>
                  </div>
                  {activeTab === "your-images" && (
                    <label
                      style={{
                        alignItems: "center",
                        backgroundColor: uploading ? "#F3F4F6" : "#EEF2FF",
                        border: "1px solid",
                        borderColor: uploading ? "#E5E7EB" : "#C7D2FE",
                        borderRadius: "4px",
                        color: uploading ? "#9CA3AF" : "#6366F1",
                        cursor: uploading ? "not-allowed" : "pointer",
                        display: "flex",
                        fontSize: "12px",
                        fontWeight: 500,
                        gap: "6px",
                        padding: "6px 12px",
                      }}
                    >
                      {uploading ? (
                        <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
                      ) : (
                        <Upload size={14} />
                      )}
                      {uploading ? (uploadStatus ?? "Uploading...") : "Upload images"}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleUpload}
                        disabled={uploading}
                        style={{ display: "none" }}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div
                  style={{
                    backgroundColor: "#FEF2F2",
                    borderBottom: "1px solid #FEE2E2",
                    color: "#DC2626",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                >
                  {error}
                </div>
              )}

              {/* Image Grid - Your Images Tab */}
              {activeTab === "your-images" && (
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "12px",
                  }}
                >
                  {loading ? (
                    <div
                      style={{
                        alignItems: "center",
                        color: "#9CA3AF",
                        display: "flex",
                        justifyContent: "center",
                        padding: "24px",
                      }}
                    >
                      <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
                      <span style={{ fontSize: "13px", marginLeft: "8px" }}>Loading images...</span>
                    </div>
                  ) : (assets.length === 0 ? (
                    <div
                      style={{
                        color: "#9CA3AF",
                        fontSize: "13px",
                        padding: "24px",
                        textAlign: "center",
                      }}
                    >
                      No images found. Upload one to get started.
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gap: "8px",
                        gridTemplateColumns: "repeat(3, 1fr)",
                      }}
                    >
                      {assets.map((asset) => {
                        const imageUrl = getImageUrl(asset),
                          isSelected = value === imageUrl;
                        return (
                          <button
                            key={asset.id}
                            type="button"
                            onClick={() => {
                              handleSelect(asset);
                            }}
                            style={{
                              aspectRatio: "1",
                              backgroundColor: "#F9FAFB",
                              border: "2px solid",
                              borderColor: isSelected ? "#6366F1" : "#E5E7EB",
                              borderRadius: "6px",
                              cursor: "pointer",
                              overflow: "hidden",
                              padding: 0,
                              position: "relative",
                            }}
                          >
                            <img
                              crossOrigin="anonymous"
                              src={imageUrl}
                              alt={asset.filename || "Asset"}
                              style={{
                                height: "100%",
                                objectFit: "cover",
                                width: "100%",
                              }}
                            />
                            {isSelected && (
                              <div
                                style={{
                                  alignItems: "center",
                                  backgroundColor: "#6366F1",
                                  borderRadius: "50%",
                                  display: "flex",
                                  height: "20px",
                                  justifyContent: "center",
                                  position: "absolute",
                                  right: "4px",
                                  top: "4px",
                                  width: "20px",
                                }}
                              >
                                <Check size={12} color="white" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {/* Image Grid - Stock Library Tab */}
              {activeTab === "stock-images" && (
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "12px",
                  }}
                >
                  {/* The library spans every trade, so an unfiltered grid shows a
                      plumber photos of lawn care. */}
                  {tradeOptions.length > 0 && (
                    <select
                      value={tradeFilter}
                      onChange={(e) => {
                        setTradeFilter(e.target.value);
                        void fetchStockImages(e.target.value);
                      }}
                      style={{
                        background: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: "6px",
                        color: "#374151",
                        fontSize: "13px",
                        marginBottom: "10px",
                        padding: "6px 8px",
                        width: "100%",
                      }}
                    >
                      <option value={ALL_TRADES}>All trades</option>
                      {tradeOptions.map((trade) => (
                        <option key={trade.id} value={trade.id}>
                          {trade.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {stockLoading ? (
                    <div
                      style={{
                        alignItems: "center",
                        color: "#9CA3AF",
                        display: "flex",
                        justifyContent: "center",
                        padding: "24px",
                      }}
                    >
                      <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
                      <span style={{ fontSize: "13px", marginLeft: "8px" }}>
                        Loading stock images...
                      </span>
                    </div>
                  ) : (stockImages.length === 0 ? (
                    <div
                      style={{
                        color: "#9CA3AF",
                        fontSize: "13px",
                        padding: "24px",
                        textAlign: "center",
                      }}
                    >
                      No stock images available yet.
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gap: "8px",
                        gridTemplateColumns: "repeat(3, 1fr)",
                      }}
                    >
                      {stockImages.map((image) => {
                        const imageUrl = getStockImageUrl(image),
                          isSelected = value === imageUrl;
                        return (
                          <button
                            key={image.id}
                            type="button"
                            onClick={() => {
                              handleStockSelect(image);
                            }}
                            style={{
                              aspectRatio: "1",
                              backgroundColor: "#F9FAFB",
                              border: "2px solid",
                              borderColor: isSelected ? "#6366F1" : "#E5E7EB",
                              borderRadius: "6px",
                              cursor: "pointer",
                              overflow: "hidden",
                              padding: 0,
                              position: "relative",
                            }}
                          >
                            <img
                              crossOrigin="anonymous"
                              src={imageUrl}
                              alt={image.metadata?.altText || image.filename || "Stock image"}
                              style={{
                                height: "100%",
                                objectFit: "cover",
                                width: "100%",
                              }}
                            />
                            {isSelected && (
                              <div
                                style={{
                                  alignItems: "center",
                                  backgroundColor: "#6366F1",
                                  borderRadius: "50%",
                                  display: "flex",
                                  height: "20px",
                                  justifyContent: "center",
                                  position: "absolute",
                                  right: "4px",
                                  top: "4px",
                                  width: "20px",
                                }}
                              >
                                <Check size={12} color="white" />
                              </div>
                            )}
                            {image.metadata?.category && (
                              <div
                                style={{
                                  backgroundColor: "rgba(0,0,0,0.6)",
                                  bottom: "0",
                                  color: "white",
                                  fontSize: "9px",
                                  left: "0",
                                  padding: "2px 4px",
                                  position: "absolute",
                                  right: "0",
                                  textAlign: "center",
                                  textTransform: "capitalize",
                                }}
                              >
                                {image.metadata.category}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {/* Manual URL Input */}
              <div
                style={{
                  borderTop: "1px solid #E5E7EB",
                  display: "flex",
                  gap: "8px",
                  padding: "8px 12px",
                }}
              >
                <input
                  type="text"
                  placeholder="Or paste image URL..."
                  value={value}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  style={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "4px",
                    flex: 1,
                    fontSize: "12px",
                    outline: "none",
                    padding: "6px 10px",
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </FieldLabel>
  );
};

export { ImagePicker };
